import React, { useEffect, useMemo, useState } from 'react';
import TrackerIcon from './TrackerIcon';
import TrackerModal from './TrackerModal';
import { TrackerCatalyst, TrackerPermissions, TrackerSearchDoc } from '../../types';
import { isMine, perspSummary } from './readThrough';
import { fmtDay } from './helpers';
import { catalystTrackerApi } from '../../services/catalystTrackerApi';
import { ACTIVE_SEARCH_STATUSES, cleanTerm, deepLeft, deepNext, fmtTime, hhmm, queuedMsg, slugOf } from './searchFlow';
import PerspectiveRequestModal from './PerspectiveRequestModal';

interface Props {
  rows: TrackerCatalyst[];
  company: string;
  only: boolean;
  editing: boolean;
  onEditingChange: (open: boolean) => void;
  onApply: (company: string, only: boolean) => void;
  onOff: () => void;
  permissions: TrackerPermissions | null;
  searchLog: string[] | undefined;
  onRecordSearch: () => void;
}

const POLL_MS = 8000;

/** Polls one search doc while it's outstanding (tracker.js's live `searches` Map, here a plain poll
 * since there's no realtime push channel in this Flask/Postgres backend). */
function useSearchPoll(slug: string | null): TrackerSearchDoc | null {
  const [doc, setDoc] = useState<TrackerSearchDoc | null>(null);

  useEffect(() => {
    if (!slug) { setDoc(null); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = () => {
      catalystTrackerApi.getSearch(slug).then((d) => {
        if (cancelled) return;
        setDoc(d?.data || null);
        if (d?.data && (ACTIVE_SEARCH_STATUSES as readonly string[]).includes(d.data.status)) {
          timer = setTimeout(tick, POLL_MS);
        }
      }).catch(() => {});
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [slug]);

  return doc;
}

/** "Viewing as <company>" bar + the company picker, matching Ryan's #perspPanel/#perspDlg (tracker.js's
 * openPersp/renderPerspPanel). "Relevant only" and the summary counts run on the full read-through/
 * Perspective engine (readThrough.ts) — isMine()/perspOf() exactly as Ryan wrote them. The live
 * "look it up from public sources" lookup (for an untracked company) and the general-scan/per-catalyst
 * "request a check" flow (via the "+" button, PerspectiveRequestModal) both queue a real worker search
 * through catalystTrackerApi.fireTrigger() — see searchFlow.ts's header for what's faithfully ported vs.
 * the original's dead Firestore-only code path. */
const PerspectivePanel: React.FC<Props> = ({ rows, company, only, editing, onEditingChange, onApply, onOff, permissions, searchLog, onRecordSearch }) => {
  const [draft, setDraft] = useState(company);
  const [draftOnly, setDraftOnly] = useState(only);
  const [findOpen, setFindOpen] = useState(false);
  const [lookupMsg, setLookupMsg] = useState('');

  const companies = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => { if (r.company) set.add(r.company); });
    return Array.from(set).sort();
  }, [rows]);

  // tracker.js's coMatches(): ticker-aware, ranked (name starts-with > a word starts-with > ticker
  // starts-with > plain substring), so "PFE" finds Pfizer and "AZ" ranks AstraZeneca above a substring hit.
  const tickerByCompany = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => { if (r.company && r.ticker && r.ticker !== 'Private') m.set(r.company, r.ticker); });
    return m;
  }, [rows]);

  const suggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (q.length < 2) return [];
    return companies
      .map((c) => {
        const cl = c.toLowerCase();
        const t = (tickerByCompany.get(c) || '').toLowerCase();
        const sc = cl.startsWith(q) ? 0 : cl.split(/[\s(]+/).some((w) => w.startsWith(q)) ? 1 : t.startsWith(q) ? 2 : cl.includes(q) ? 3 : 9;
        return { c, t: tickerByCompany.get(c) || '', sc };
      })
      .filter((x) => x.sc < 9)
      .sort((a, b) => a.sc - b.sc || a.c.localeCompare(b.c))
      .slice(0, 8);
  }, [companies, tickerByCompany, draft]);

  useEffect(() => {
    if (editing) { setDraft(company); setDraftOnly(only); setLookupMsg(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const submit = () => {
    const picked = draft.trim();
    if (!picked) return;
    onApply(picked, draftOnly);
    onEditingChange(false);
  };

  const tracked = useMemo(() => !!company && rows.some((r) => isMine(r, company)), [rows, company]);
  const summary = useMemo(() => (company && tracked ? perspSummary(rows, company) : null), [rows, company, tracked]);

  // renderPerspPanel()'s not-tracked branch (tracker.js:664-673): poll the company-name slug's search doc.
  const lookupSlug = company && !tracked ? slugOf(company) : null;
  const lookupSearch = useSearchPoll(lookupSlug);
  const lookupBusy = !!lookupSearch && (ACTIVE_SEARCH_STATUSES as readonly string[]).includes(lookupSearch.status);

  const runLookup = () => {
    if (!permissions?.editor) { setLookupMsg('Looking up a new company needs editor access. Ask an editor to add it.'); return; }
    const left = deepLeft(searchLog);
    if (!left) { setLookupMsg(`You've used your 3 source searches this hour. Next one at ${hhmm(deepNext(searchLog))}.`); return; }
    const name = cleanTerm(company);
    const slug = slugOf(name);
    const text = `SEARCH_ID: ${slug}\nKEYWORD: ${name}\nFOCUS: COMPANY LOOKUP for the Perspective feature: find this company's oncology/hematology pipeline from public sources (IR site, filings, press releases, registries) and add its dated catalysts (upcoming and the last 12 months) with sources; use the company's official name.`;
    catalystTrackerApi.fireTrigger(text).then(() => {
      onRecordSearch();
      setLookupMsg('');
    }).catch(() => setLookupMsg("Couldn't queue the lookup just now. Try again shortly."));
  };

  let lookupPanel: React.ReactNode = null;
  if (company && !tracked) {
    let msg: React.ReactNode;
    let showLookupBtn = false;
    if (lookupBusy) {
      msg = lookupSearch!.status === 'running'
        ? <>Looking up <b>{company}</b> in public sources now. Its catalysts and the annotations appear here once verified.</>
        : <>{queuedMsg('Lookup queued')}{lookupSearch!.requestedAt ? ` (requested ${fmtTime(lookupSearch!.requestedAt)})` : ''}. <b>{company}</b>: its catalysts and the annotations appear here once verified.</>;
    } else if (lookupSearch?.status === 'done') {
      msg = <>The source search for <b>{company}</b> finished without adding verified catalysts{lookupSearch.note ? `: ${lookupSearch.note}` : ''}. Check the spelling or pick another company.</>;
      showLookupBtn = true;
    } else if (lookupSearch?.status === 'failed') {
      msg = <>The lookup for <b>{company}</b> didn&rsquo;t finish. Try again later.</>;
      showLookupBtn = true;
    } else {
      msg = <><b>{company}</b> isn&rsquo;t tracked yet.</>;
      showLookupBtn = true;
    }
    lookupPanel = (
      <div className="ppanel">
        {lookupBusy && lookupSearch!.status === 'running' ? <span className="spin" aria-hidden="true" /> : <span className="lbl"><TrackerIcon name="eye" /></span>}
        <span className="psum">
          {msg}
          {showLookupBtn && permissions?.editor && (
            <>{' '}<button type="button" className="ib sm" style={{ marginLeft: 6 }} onClick={runLookup}>
              <TrackerIcon name="flask" />Look it up from public sources
            </button></>
          )}
          {lookupMsg && <div className="note" style={{ marginTop: 4 }}>{lookupMsg}</div>}
        </span>
        <span className="pact">
          <button type="button" className="ib sm" title="Change company" onClick={() => onEditingChange(true)}><TrackerIcon name="pencil" /></button>
          <button type="button" className="ib sm tip-r" title="Turn off perspective" aria-label="Turn off perspective" onClick={onOff}><TrackerIcon name="x" /></button>
        </span>
      </div>
    );
  }

  return (
    <>
      {lookupPanel}

      {company && tracked && summary && (
        <div className="ppanel">
          <span className="lbl"><TrackerIcon name="eye" /> Viewing as <b>{company}</b></span>
          <span className="psum">
            <b>{summary.own}</b> of your upcoming catalysts · <b>{summary.pastAffected}</b> readouts affected you (12 mo) · <b>{summary.futureCould}</b> upcoming could affect you
            {summary.watch.length > 0 && (
              <> · watch: {summary.watch.map((r, i) => (
                <React.Fragment key={r.id}>
                  {i > 0 && ' · '}
                  <span>{r.asset} {r.window || fmtDay(r.date)}</span>
                </React.Fragment>
              ))}</>
            )}
          </span>
          <span className="pact">
            <button
              type="button"
              className="ib sm"
              title="A catalyst should affect this company but isn't flagged? Request a check"
              onClick={() => setFindOpen(true)}
            >
              <TrackerIcon name="plus" />
            </button>
            <button
              type="button"
              className="ib sm"
              aria-pressed={only}
              title={only ? `Showing only catalysts relevant to ${company} · click to show all` : `Filter to catalysts relevant to ${company}`}
              aria-label="Filter to relevant catalysts"
              onClick={() => onApply(company, !only)}
            >
              Relevant only
            </button>
            <button type="button" className="ib sm" title="Change company" onClick={() => onEditingChange(true)}>
              <TrackerIcon name="pencil" />
            </button>
            <button type="button" className="ib sm tip-r" title="Turn off perspective" aria-label="Turn off perspective" onClick={onOff}>
              <TrackerIcon name="x" />
            </button>
          </span>
        </div>
      )}

      {editing && (
        <TrackerModal
          title="Perspective"
          onClose={() => onEditingChange(false)}
          footer={(
            <div className="full" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, width: '100%' }}>
              {company ? <button type="button" className="ib" onClick={() => { onOff(); onEditingChange(false); }}>Turn off</button> : <span />}
              <button type="button" className="ib primary" onClick={submit}>Apply</button>
            </div>
          )}
        >
          <p className="note" style={{ margin: '0 0 10px' }}>
            Type your company. Every catalyst is then annotated with how it affects that company&rsquo;s drugs: what past
            results did to your pending readouts, and for upcoming competitor events, what happens to you if they are
            positive or negative.
          </p>
          <div className="form">
            <label className="full" style={{ position: 'relative' }}>
              Company
              <input
                autoComplete="off"
                placeholder="Start typing a company name or ticker"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              {suggestions.length > 0 && (
                <div className="psug" role="listbox">
                  {suggestions.map((s) => (
                    <button type="button" key={s.c} role="option" className="psi" onClick={() => setDraft(s.c)}><b>{s.c}</b>{s.t && <span>{s.t}</span>}</button>
                  ))}
                </div>
              )}
            </label>
            <label className="inline full">
              <input type="checkbox" checked={draftOnly} onChange={(e) => setDraftOnly(e.target.checked)} />
              Show only catalysts relevant to this company
            </label>
          </div>
        </TrackerModal>
      )}

      {findOpen && company && (
        <PerspectiveRequestModal
          rows={rows}
          company={company}
          permissions={permissions}
          searchLog={searchLog}
          onRecordSearch={onRecordSearch}
          onClose={() => setFindOpen(false)}
        />
      )}
    </>
  );
};

export default PerspectivePanel;
